import React, { useState, useEffect } from 'react';
import { 
  Search, 
  Filter, 
  Grid, 
  List, 
  Edit2, 
  Trash2, 
  Copy, 
  Eye, 
  Clock,
  TrendingUp,
  Loader,
  AlertCircle,
  X,
  Sparkles
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-toastify';

const EmailTemplateSelector = ({ onSelectTemplate, onEditTemplate }) => {
  // State Management
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [hoveredTemplate, setHoveredTemplate] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  // Fetch templates from backend
  useEffect(() => {
    fetchTemplates();
  }, [selectedCategory, sortBy, sortOrder]);

  const fetchTemplates = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/api/comms-marketing/templates`,
        {
          params: {
            category: selectedCategory !== 'All' ? selectedCategory : undefined,
            sortBy,
            order: sortOrder
          }
        }
      );
      
      setTemplates(response.data.templates || []);
      toast.success(`Loaded ${response.data.templates.length} templates`);
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError(err.message);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  // Handle template selection
  const handleSelectTemplate = (template) => {
    if (onSelectTemplate) {
      onSelectTemplate(template);
      toast.success(`Template "${template.name}" loaded!`);
    }
  };

  // Handle template editing
  const handleEditTemplate = (template) => {
    if (onEditTemplate) {
      onEditTemplate(template);
    }
  };

  // Handle template deletion
  const handleDeleteTemplate = async (templateId, templateName) => {
    if (!window.confirm(`Delete "${templateName}"? It will be moved to trash.`)) {
      return;
    }
    
    try {
      await axios.delete(
        `${import.meta.env.VITE_API_URL}/api/comms-marketing/templates/${templateId}`
      );
      toast.success('Template moved to trash');
      fetchTemplates(); // Refresh list
    } catch (err) {
      console.error('Error deleting template:', err);
      toast.error(err.response?.data?.error || 'Failed to delete template');
    }
  };

  // Handle template duplication
  const handleDuplicateTemplate = async (templateId, templateName) => {
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/api/comms-marketing/templates/${templateId}/duplicate`
      );
      toast.success(`Template "${templateName}" duplicated!`);
      fetchTemplates(); // Refresh list
    } catch (err) {
      console.error('Error duplicating template:', err);
      toast.error('Failed to duplicate template');
    }
  };

  // Filter templates by search
  const filteredTemplates = templates.filter(template => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      template.name.toLowerCase().includes(searchLower) ||
      (template.description && template.description.toLowerCase().includes(searchLower)) ||
      template.subject.toLowerCase().includes(searchLower)
    );
  });

  // Categories
  const categories = [
    { name: 'All', emoji: '📧', color: 'blue' },
    { name: 'Gym', emoji: '💪', color: 'blue' },
    { name: 'Salon', emoji: '💇‍♀️', color: 'green' },
    { name: 'Spa', emoji: '🌸', color: 'pink' }
  ];

  return (
    <div className="email-template-selector bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl p-6 min-h-[600px]">
      {/* HEADER */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent flex items-center gap-2">
              <Sparkles className="w-8 h-8 text-purple-600" />
              Email Templates
            </h2>
            <p className="text-gray-600 mt-1">
              Choose a template or start from scratch
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* View Mode Toggle */}
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg transition ${
                viewMode === 'grid'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
              title="Grid View"
            >
              <Grid size={20} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg transition ${
                viewMode === 'list'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
              title="List View"
            >
              <List size={20} />
            </button>
          </div>
        </div>

        {/* SEARCH & FILTERS */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Search Bar */}
          <div className="lg:col-span-2 relative">
            <Search 
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" 
              size={20} 
            />
            <input
              type="text"
              placeholder="Search templates by name, description, or subject..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white border-2 border-gray-200 rounded-xl
                       focus:border-blue-500 focus:ring-4 focus:ring-blue-100 
                       transition-all duration-200 placeholder-gray-400"
            />
          </div>

          {/* Sort Dropdown */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-4 py-3 bg-white border-2 border-gray-200 rounded-xl
                     focus:border-blue-500 focus:ring-4 focus:ring-blue-100
                     transition-all duration-200 cursor-pointer"
          >
            <option value="createdAt">Recently Created</option>
            <option value="usageCount">Most Used</option>
            <option value="name">Name (A-Z)</option>
            <option value="updatedAt">Recently Updated</option>
          </select>
        </div>

        {/* CATEGORY PILLS */}
        <div className="flex items-center gap-2 mt-4 flex-wrap">
          {categories.map((category) => (
            <button
              key={category.name}
              onClick={() => setSelectedCategory(category.name)}
              className={`px-5 py-2.5 rounded-full font-semibold text-sm transition-all duration-300
                        flex items-center gap-2 ${
                selectedCategory === category.name
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg scale-105'
                  : 'bg-white text-gray-700 hover:bg-gray-100 hover:shadow-md'
              }`}
            >
              <span className="text-lg">{category.emoji}</span>
              <span>{category.name}</span>
              {category.name !== 'All' && (
                <span className="ml-1 px-2 py-0.5 bg-white/20 rounded-full text-xs">
                  {templates.filter(t => t.category === category.name).length}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* LOADING STATE */}
      {loading && (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <Loader className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-600 font-medium">Loading templates...</p>
          </div>
        </div>
      )}

      {/* ERROR STATE */}
      {error && !loading && (
        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6 text-center">
          <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-red-900 mb-2">Failed to Load Templates</h3>
          <p className="text-red-700 mb-4">{error}</p>
          <button
            onClick={fetchTemplates}
            className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
          >
            Retry
          </button>
        </div>
      )}

      {/* EMPTY STATE */}
      {!loading && !error && filteredTemplates.length === 0 && (
        <div className="bg-white border-2 border-dashed border-gray-300 rounded-xl p-12 text-center">
          <div className="text-6xl mb-4">📧</div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No Templates Found</h3>
          <p className="text-gray-600 mb-6">
            {searchTerm 
              ? `No templates match "${searchTerm}"`
              : 'No templates available in this category'
            }
          </p>
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
            >
              Clear Search
            </button>
          )}
        </div>
      )}

      {/* TEMPLATES GRID/LIST */}
      {!loading && !error && filteredTemplates.length > 0 && (
        <>
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredTemplates.map((template) => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  onSelect={handleSelectTemplate}
                  onEdit={handleEditTemplate}
                  onDelete={handleDeleteTemplate}
                  onDuplicate={handleDuplicateTemplate}
                  onPreview={setPreviewTemplate}
                  isHovered={hoveredTemplate === template.id}
                  onHover={setHoveredTemplate}
                />
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {filteredTemplates.map((template) => (
                <TemplateListItem
                  key={template.id}
                  template={template}
                  onSelect={handleSelectTemplate}
                  onEdit={handleEditTemplate}
                  onDelete={handleDeleteTemplate}
                  onDuplicate={handleDuplicateTemplate}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* BLANK TEMPLATE BUTTON */}
      {!loading && !error && (
        <div className="mt-6 p-6 bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-dashed border-purple-300 rounded-xl">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-purple-900 mb-1">
                ✨ Start from Scratch
              </h3>
              <p className="text-purple-700 text-sm">
                Create a custom email without using a template
              </p>
            </div>
            <button
              onClick={() => onSelectTemplate(null)}
              className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white 
                       rounded-xl hover:shadow-2xl hover:scale-105 transition-all duration-300
                       font-semibold"
            >
              Blank Template
            </button>
          </div>
        </div>
      )}

      {/* PREVIEW MODAL */}
      {previewTemplate && (
        <TemplatePreviewModal
          template={previewTemplate}
          onClose={() => setPreviewTemplate(null)}
          onSelect={handleSelectTemplate}
        />
      )}
    </div>
  );
};

// TEMPLATE CARD COMPONENT (Grid View)
const TemplateCard = ({ 
  template, 
  onSelect, 
  onEdit, 
  onDelete, 
  onDuplicate, 
  onPreview,
  isHovered,
  onHover
}) => {
  const categoryColors = {
    Gym: { gradient: 'from-blue-500 to-blue-600', bg: 'bg-blue-100', text: 'text-blue-800' },
    Salon: { gradient: 'from-green-500 to-green-600', bg: 'bg-green-100', text: 'text-green-800' },
    Spa: { gradient: 'from-pink-500 to-pink-600', bg: 'bg-pink-100', text: 'text-pink-800' },
    General: { gradient: 'from-gray-500 to-gray-600', bg: 'bg-gray-100', text: 'text-gray-800' }
  };

  const colors = categoryColors[template.category] || categoryColors.General;

  return (
    <div
      onMouseEnter={() => onHover(template.id)}
      onMouseLeave={() => onHover(null)}
      className="relative group bg-white rounded-xl overflow-hidden border-2 border-gray-200
                 hover:border-blue-500 hover:shadow-2xl transition-all duration-300
                 hover:-translate-y-2"
    >
      {/* GRADIENT OVERLAY ON HOVER */}
      <div className={`absolute inset-0 bg-gradient-to-br ${colors.gradient} 
                      opacity-0 group-hover:opacity-10 transition-opacity duration-300`}
      ></div>

      {/* LIVE HTML PREVIEW */}
      <div className="relative h-48 overflow-hidden bg-gray-50 border-b border-gray-200">
        <iframe
          srcDoc={template.htmlContent}
          className="w-full h-full pointer-events-none scale-[0.3] origin-top-left"
          style={{ width: '333%', height: '333%' }}
          title={`Preview of ${template.name}`}
          sandbox="allow-same-origin"
        />
        
        {/* OVERLAY ACTIONS */}
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 
                        transition-opacity duration-300 flex items-center justify-center gap-2">
          <button
            onClick={() => onPreview(template)}
            className="px-4 py-2 bg-white text-gray-900 rounded-lg hover:bg-gray-100 
                     transition flex items-center gap-2 font-medium"
            title="Preview"
          >
            <Eye size={16} />
            Preview
          </button>
          <button
            onClick={() => onSelect(template)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 
                     transition flex items-center gap-2 font-medium"
            title="Use Template"
          >
            Use Template
          </button>
        </div>
      </div>

      {/* CARD CONTENT */}
      <div className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <h3 className="font-bold text-gray-900 text-sm mb-1 line-clamp-1">
              {template.name}
            </h3>
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`px-2 py-1 rounded-full text-xs font-semibold ${colors.bg} ${colors.text}`}>
                {template.category}
              </span>
              {template.isDefault && (
                <span className="px-2 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800">
                  Default
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Subject Preview */}
        <p className="text-xs text-gray-600 mb-3 line-clamp-2">
          <strong>Subject:</strong> {template.subject}
        </p>

        {/* Stats */}
        <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
          <div className="flex items-center gap-1" title="Usage Count">
            <TrendingUp size={12} />
            <span>{template.usageCount || 0} uses</span>
          </div>
          <div className="flex items-center gap-1" title="Last Modified">
            <Clock size={12} />
            <span>{new Date(template.updatedAt).toLocaleDateString()}</span>
          </div>
        </div>

        {/* ACTION BUTTONS */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => onEdit(template)}
            className="flex-1 px-3 py-2 bg-gradient-to-r from-green-500 to-emerald-600 
                     text-white rounded-lg hover:shadow-lg transition text-xs font-semibold
                     flex items-center justify-center gap-1"
            title="Edit Template"
          >
            <Edit2 size={14} />
            Edit
          </button>
          <button
            onClick={() => onDuplicate(template.id, template.name)}
            className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 
                     transition"
            title="Duplicate"
          >
            <Copy size={14} />
          </button>
          {!template.isDefault && (
            <button
              onClick={() => onDelete(template.id, template.name)}
              className="px-3 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 
                       transition"
              title="Delete"
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// TEMPLATE LIST ITEM COMPONENT (List View)
const TemplateListItem = ({ template, onSelect, onEdit, onDelete, onDuplicate }) => {
  const categoryColors = {
    Gym: 'text-blue-600',
    Salon: 'text-green-600',
    Spa: 'text-pink-600',
    General: 'text-gray-600'
  };

  return (
    <div className="bg-white rounded-lg border-2 border-gray-200 p-4 hover:border-blue-500 
                    hover:shadow-lg transition-all duration-200 flex items-center gap-4">
      {/* Mini Preview */}
      <div className="w-32 h-20 bg-gray-50 rounded-lg overflow-hidden border border-gray-200 flex-shrink-0">
        <iframe
          srcDoc={template.htmlContent}
          className="w-full h-full pointer-events-none scale-[0.15] origin-top-left"
          style={{ width: '666%', height: '500%' }}
          title={`Preview of ${template.name}`}
          sandbox="allow-same-origin"
        />
      </div>

      {/* Info */}
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <h3 className="font-bold text-gray-900">{template.name}</h3>
          <span className={`text-xs font-semibold ${categoryColors[template.category]}`}>
            {template.category}
          </span>
          {template.isDefault && (
            <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-800">
              Default
            </span>
          )}
        </div>
        <p className="text-sm text-gray-600 line-clamp-1 mb-2">
          <strong>Subject:</strong> {template.subject}
        </p>
        <div className="flex items-center gap-4 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <TrendingUp size={12} />
            {template.usageCount || 0} uses
          </span>
          <span className="flex items-center gap-1">
            <Clock size={12} />
            Updated {new Date(template.updatedAt).toLocaleDateString()}
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          onClick={() => onSelect(template)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-medium"
        >
          Use
        </button>
        <button
          onClick={() => onEdit(template)}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm font-medium"
        >
          Edit
        </button>
        <button
          onClick={() => onDuplicate(template.id, template.name)}
          className="p-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
          title="Duplicate"
        >
          <Copy size={16} />
        </button>
        {!template.isDefault && (
          <button
            onClick={() => onDelete(template.id, template.name)}
            className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition"
            title="Delete"
          >
            <Trash2 size={16} />
          </button>
        )}
      </div>
    </div>
  );
};

// TEMPLATE PREVIEW MODAL
const TemplatePreviewModal = ({ template, onClose, onSelect }) => {
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
         onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
           onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-blue-50 to-purple-50">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{template.name}</h2>
            <p className="text-sm text-gray-600 mt-1">
              {template.category} • Subject: {template.subject}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition"
          >
            <X size={24} />
          </button>
        </div>

        {/* Preview Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
          <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-lg overflow-hidden">
            <iframe
              srcDoc={template.htmlContent}
              className="w-full"
              style={{ height: '600px', border: 'none' }}
              title={`Full preview of ${template.name}`}
              sandbox="allow-same-origin"
            />
          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-6 border-t border-gray-200 bg-white flex items-center justify-between">
          <div className="text-sm text-gray-600">
            Last modified: {new Date(template.updatedAt).toLocaleString()}
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition font-medium"
            >
              Close
            </button>
            <button
              onClick={() => {
                onSelect(template);
                onClose();
              }}
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white 
                       rounded-lg hover:shadow-xl transition font-semibold"
            >
              Use This Template
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailTemplateSelector;